import React, { useEffect, useState } from 'react';
import { useAuth } from 'services/auth-service';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import OrganizationIcon from 'assets/Icons/sidebarIcons/OrganizationIcon';
import { useQuery } from 'react-query';
import { IntegrationDetails, OrganizationProps } from 'services/hooks/organization-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveOrg from './crud/useSave';
import BuildingService from 'services/hooks/building-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import { CardFormat } from 'utils/constants';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getOrgResult, setGetOrgResult] = useState<OrganizationProps | null>(null);
  const [integrationResult, setIntegrationResult] = useState<IntegrationProps | null>(null);
  const [isAVLIntegration, setIsAVLIntegration] = useState(false);

  const {
    state: { user }
  } = useAuth();

  // validation
  const CreateOrgSchema = yup.object().shape({
    buildingUuid: yup.string().when([], {
      is: () => user.role !== 'BUILDING',
      then: yup.string().required('Building is required'),
      otherwise: yup.string().notRequired()
    }),
    name: yup.string().required('Name is required')
  });

  const { isSuccess, data } = useQuery<OrganizationProps, Error>(
    'organization',
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  const methods = useForm({
    defaultValues: getOrgResult
      ? {
          uuid: getOrgResult.uuid,
          name: getOrgResult.name,
          integrationUuid: getOrgResult.integrationUuid,
          integrationDetails: {
            acsSystemId: getOrgResult?.integrationDetails?.acsSystemId || ''
          }
        }
      : {
          uuid: '',
          name: '',
          integrationUuid: '',
          integrationDetails: {
            acsSystemId: ''
          }
        },
    resolver: yupResolver(CreateOrgSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      setGetOrgResult(data);
      methods.reset(data);
    }
  }, [data, editUUID, methods]);

  const saveOrgMutation = useSaveOrg({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Organization'));
      } else {
        toast.success(generateUpdateSuccessMessage('Organization'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: () => {
      if (!editUUID) {
        toast.error(generateSaveErrorMessage('Organization'));
      } else {
        toast.error(generateUpdateErrorMessage('Organization'));
      }
    }
  });

  // Displaying `Enter ACS System Id` field when selected Integration is `AVL`.
  const integrationUuidWatch = methods.watch('integrationUuid');

  const { data: integrationData, refetch: refetchIntegrationData } = useQuery<
    IntegrationProps,
    Error
  >(
    ['integration', integrationUuidWatch],
    () => IntegrationService.findIntegrationById(integrationUuidWatch),
    {
      enabled: integrationUuidWatch ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration'));
      }
    }
  );

  useEffect(() => {
    // Refetch Integration data when `integrationUuidWatch` changes
    if (integrationUuidWatch) {
      refetchIntegrationData();
    }

    // Set state of integrationData
    if (integrationData) {
      setIntegrationResult(integrationData);
    } else {
      /* The below code is setting the state of `integrationResult` to null. This is done when the `integrationUuid` is not selected 
      in the form, so it clears the previous value of `integrationResult`. */
      setIntegrationResult(null);
    }

    if (integrationResult?.name === CardFormat.AVL) {
      setIsAVLIntegration(true);
    } else {
      setIsAVLIntegration(false);
    }
  }, [integrationData, integrationResult, integrationUuidWatch, refetchIntegrationData]);

  const onSubmit = async (e: OrganizationProps) => {
    let buildingID = '';
    if (user.role === 'SUPER' || user.role === 'ACCOUNT') {
      buildingID = e?.buildingUuid || '';
    } else {
      buildingID = user?.uuid ? user.uuid : '';
    }

    const integrationDetails: IntegrationDetails = {
      acsSystemId: e?.integrationDetails?.acsSystemId
    };

    saveOrgMutation.mutate({
      uuid: e?.uuid,
      name: e?.name,
      buildingUuid: buildingID,
      bitformat: e?.bitformat ? e.bitformat : 'W26-Pier',
      integrationUuid: e?.integrationUuid ? e?.integrationUuid : undefined,
      integrationDetails: integrationDetails
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {(user.role === 'SUPER' || user.role === 'ACCOUNT') && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose a Building"
                placeholder="Select Building"
                queryname="buildings"
                name="buildingUuid"
                errors={methods.formState.errors}
                disabled={editUUID && true}
                query={BuildingService.findAllBuildings}
                defaultValue=""
              />
            )}
            <Input
              label="Organization Name"
              name="name"
              type="text"
              // data-testid="example"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Integration"
              placeholder="Select an Integration"
              queryname="integrations"
              name="integrationUuid"
              errors={methods.formState.errors}
              query={() => IntegrationService.findAllIntegrations()}
              expectedLabel="Integration/Name"
              multi={false}
            />
            {isAVLIntegration && (
              <Input
                label="Enter ACS System Id"
                name="integrationDetails.acsSystemId"
                type="text"
                placeholder="Enter ACS System Id"
                autoFocus
                methods={methods}
              />
            )}
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            // onClick={onClick}
            iconLeft={<OrganizationIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            // disabled={buttonDisabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
